/** Bağımsız vitrin asistanı loader'ı; cache'li ayarı alır ve ana paketi gerektiğinde asenkron yükler. */
import {isAdminAssistantHost} from './config/page-context.js';
import {allowsAssistantDomain} from './config/publish-scope.js';
import {assistantNonceManager} from './bot-protection/nonce-manager.js';

const LOADER_SELECTOR = 'script[data-onepage-assistant-loader]';

/** Çalışan loader script etiketini bulur. */
const currentLoaderScript = () => {
  if (document.currentScript instanceof HTMLScriptElement
    && document.currentScript.matches(LOADER_SELECTOR)) {
    return document.currentScript;
  }

  const scripts = document.querySelectorAll(LOADER_SELECTOR);
  return scripts[scripts.length - 1] || null;
};

/** Relative runtime URL'sini loader'ın geldiği uygulama origin'ine göre çözer. */
const resolveUrl = (value, base) => {
  try {
    return new URL(String(value || ''), base).toString();
  } catch {
    return '';
  }
};

/** Özel sayfa yolunu paylaşmadan tarayıcı kaynağını runtime ve nonce isteklerine ekler. */
const withPageOrigin = (value) => {
  try {
    const url = new URL(value);
    const page = new URL(globalThis.window?.location?.href);
    if (['https:', 'http:'].includes(page.protocol)) url.searchParams.set('pageUrl', page.origin);
    return url.toString();
  } catch {
    return value;
  }
};

/** Sürüm ve locale eklenmiş genel runtime config URL'sini oluşturur. */
export const runtimeConfigUrl = (script) => {
  if (!script?.src) {
    return '';
  }

  const url = resolveUrl(script.dataset.configUrl, script.src);
  if (!url) {
    return '';
  }

  const query = new URL(url);
  const version = String(script.dataset.configVersion || '').trim();
  const locale = String(script.dataset.locale || '').trim();
  if (version !== '') {
    query.searchParams.set('v', version);
  }
  if (locale !== '') {
    query.searchParams.set('locale', locale);
  }

  return withPageOrigin(query.toString());
};

/** Genel runtime ayarını browser/CDN önbelleğini kullanarak alır. */
export const loadRuntimeConfig = async (script) => {
  if(isAdminAssistantHost(window,globalThis.document,script?.src))return null;
  const url = runtimeConfigUrl(script);
  if (!url) {
    return null;
  }

  try {
    const response = await fetch(url, {
      credentials: 'omit',
      headers: {'Accept': 'application/json'},
    });
    if (!response.ok) {
      return null;
    }

    const config = await response.json();
    if (!config || typeof config !== 'object') {
      return null;
    }

    if (!allowsAssistantDomain(config)) return null;

    if (config.enabled === true
      && typeof config.abuseNonceEndpoint === 'string'
      && config.abuseNonceEndpoint !== '') {
      try {
        config.abuseNonceEndpoint = resolveUrl(config.abuseNonceEndpoint, script.src);
        await assistantNonceManager(config)?.ensure();
      } catch {
        // Arayüz açılabilir; yapılandırılmış endpoint'e istek taze nonce olmadan gönderilmez.
      }
    }

    const firstName = typeof window.firstName === 'string' ? window.firstName.trim() : '';
    if (firstName !== '' && typeof config.welcomeMessageLoggedIn === 'string'
      && config.welcomeMessageLoggedIn.trim() !== '') {
      config.welcomeMessage = config.welcomeMessageLoggedIn.split(':firstName').join(firstName);
    }

    return config;
  } catch {
    return null;
  }
};

/** Ana asistan bundle'ını config hazır olduktan sonra sayfaya bir kez ekler. */
export const loadAssistantBundle = (script, config) => {
  if(isAdminAssistantHost(window,globalThis.document,script?.src) || !allowsAssistantDomain(config))return;
  const src = resolveUrl(script?.dataset.assistantSrc, script?.src || window.location.href);
  if (!src || document.querySelector(`script[data-onepage-assistant-runtime="${src}"]`)) {
    return;
  }

  window.storefrontAssistantConfig = config;
  const bundle = document.createElement('script');
  bundle.async = true;
  bundle.src = src;
  bundle.dataset.onepageAssistantRuntime = src;
  document.head.appendChild(bundle);
};

if (typeof document !== 'undefined') {
  const script = currentLoaderScript();
  loadRuntimeConfig(script).then((config) => {
    if (config?.enabled === true) {
      loadAssistantBundle(script, config);
    }
  });
}
